import type { IconName } from '@/src/components/ui/Icon'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { Button } from '@/src/components/ui/Button'
import { Icon, symbolName } from '@/src/components/ui/Icon'
import { colors } from '@/src/theme'
import { body3, headline1, label2 } from '@/src/theme/typography'
import { useTranslation } from 'react-i18next'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

const PROGRESS_BAR_HEIGHT = 9

type InstructionItem = {
  key: InstructionKey
  iconName: IconName
}

type InstructionKey =
  'quiet_environment' | 'posture' | 'breath' | 'vowel' | 'voice' | 'listen'

const INSTRUCTION_ITEMS: InstructionItem[] = [
  {
    key: 'quiet_environment',
    iconName: symbolName('bell.slash', 'notifications_off'),
  },
  {
    key: 'posture',
    iconName: symbolName('figure.stand', 'accessibility_new'),
  },
  {
    key: 'breath',
    iconName: symbolName('lungs', 'pulmonology'),
  },
  {
    key: 'vowel',
    iconName: symbolName('person.wave.2', 'record_voice_over'),
  },
  {
    key: 'voice',
    iconName: symbolName('music.mic', 'mic'),
  },
  {
    key: 'listen',
    iconName: symbolName('pianokeys', 'piano'),
  },
]

type OnboardingTranslate = ReturnType<
  typeof useTranslation<['onboarding', 'common']>
>['t']

function getInstructionLabel(
  t: OnboardingTranslate,
  key: InstructionKey,
  recordingInstructions: string,
) {
  switch (key) {
    case 'quiet_environment':
      return t(
        'onboarding:voice_assessment.recording_instructions.quiet_environment',
      )
    case 'posture':
      return t('onboarding:voice_assessment.recording_instructions.posture')
    case 'breath':
      return t('onboarding:voice_assessment.recording_instructions.breath')
    case 'voice':
      return t('onboarding:voice_assessment.recording_instructions.voice')
    case 'listen':
      return t('onboarding:voice_assessment.recording_instructions.listen')
    case 'vowel':
      return recordingInstructions
  }
}

export interface VoiceAssessmentRecordingInstructionsStepProps {
  /** Measurement-specific recording guidance shown as the final item. */
  recordingInstructions: string
  onNext: () => void
  onSkip: () => void
  progressCurrentStep: number
  progressTotalSteps: number
}

export function VoiceAssessmentRecordingInstructionsStep({
  recordingInstructions,
  onNext,
  onSkip,
  progressCurrentStep,
  progressTotalSteps,
}: VoiceAssessmentRecordingInstructionsStepProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  const insets = useSafeAreaInsets()
  const footerPaddingBottom = Math.max(insets.bottom, 24)
  const progressSteps = Array.from(
    { length: progressTotalSteps },
    (_, index) => index + 1,
  )

  return (
    <View
      className="flex-1 bg-background-primary"
      style={{
        flex: 1,
        backgroundColor: colors.backgroundPrimary,
        paddingTop: insets.top,
      }}
    >
      <Head>
        <title>{t('onboarding:voice_assessment.page_title')}</title>
      </Head>
      <StatusBar style="dark" />

      <View className="px-6 pt-6">
        <View className="flex-row items-center justify-end mb-3">
          <Pressable onPress={onSkip} className="active:opacity-60" hitSlop={8}>
            <Text
              {...NON_SCALING_TEXT_PROPS}
              style={[label2, { color: colors.textBody }]}
            >
              {t('onboarding:voice_assessment.skip_header')}
            </Text>
          </Pressable>
        </View>
        <View className="flex-row items-center gap-2">
          {progressSteps.map((stepNumber) => (
            <View
              key={`assessment-recording-instructions-progress-${stepNumber}`}
              className={`flex-1 rounded-full ${stepNumber <= progressCurrentStep ? 'bg-primary' : 'bg-border'}`}
              style={{ height: PROGRESS_BAR_HEIGHT }}
            />
          ))}
        </View>
      </View>

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 18,
          paddingTop: 52,
          paddingBottom: 24,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View className="items-center" style={{ gap: 32, width: '100%' }}>
          <Text
            {...NON_SCALING_TEXT_PROPS}
            className="text-center"
            style={[headline1, { color: colors.textHeader, letterSpacing: 0 }]}
          >
            {t('onboarding:voice_assessment.recording_instructions_title')}
          </Text>

          <View style={{ gap: 16, width: '100%' }}>
            {INSTRUCTION_ITEMS.map((item) => (
              <View
                key={item.key}
                className="flex-row items-center rounded-[8px]"
                style={{
                  gap: 12,
                  minHeight: 64,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  backgroundColor: colors.background,
                }}
              >
                <Icon name={item.iconName} size={24} color="muted" />
                <Text
                  {...NON_SCALING_TEXT_PROPS}
                  style={[
                    body3,
                    {
                      color: colors.textBody,
                      flex: 1,
                      lineHeight: 18,
                      letterSpacing: 0,
                    },
                  ]}
                >
                  {getInstructionLabel(t, item.key, recordingInstructions)}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>

      <View
        style={{
          paddingHorizontal: 24,
          paddingBottom: footerPaddingBottom,
        }}
      >
        <Button
          label={t('onboarding:voice_assessment.recording_instructions_cta')}
          onPress={onNext}
          variant="primary"
          size="md"
          containerClassName="w-full"
        />
      </View>
    </View>
  )
}
